import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

/**
 * Wrapper around TanStack Router's useNavigate that handles query strings
 * embedded in the `to` path. TanStack Router's navigate expects query params
 * via the `search` option, not appended to the path string.
 *
 * This allows callers to keep using `navigate({ to: "/path?key=value" })`
 * without silently dropping query parameters.
 */
export function useAppNavigate() {
  const navigate = useNavigate();

  return useCallback(
    (opts: Parameters<typeof navigate>[0]) => {
      if (typeof opts.to === "string" && (opts.to.includes("?") || opts.to.includes("#"))) {
        const url = new URL(opts.to, "http://placeholder");
        // Intentionally collapses duplicate keys (a=1&a=2 -> {a:"2"}). Repeated params are not supported here.
        const search = url.search ? Object.fromEntries(url.searchParams) : undefined;
        const hash = url.hash ? url.hash.slice(1) : undefined;
        const parsed: Record<string, unknown> = { ...opts, to: url.pathname };
        if (search && !opts.search) {
          parsed.search = search;
        }
        if (hash && !opts.hash) {
          parsed.hash = hash;
        }
        return navigate(parsed as Parameters<typeof navigate>[0]);
      }
      return navigate(opts);
    },
    [navigate],
  );
}
